import * as THREE from "three";
import { randomRange, randomSign } from "./common_funcs.js";
import { Vector2 } from "./vector2.js";
import { Rect } from "./rect.js";
import { Vector3, Vector4 } from "../webgl_2d/core/types/math_types.js";
import { MoveDirection } from "./types.js";
import { IRGBColor, IRGBAColor } from "../utils/parse_color.js";
import { UIImage } from "../ui/common_ui_elements/ui_image.js";

/** Ремап значення з діапазону [v_min, v_max] в [out_min, out_max] з опціональним затисканням */
export function remap(
  value   : number,
  v_min   : number,
  v_max   : number,
  out_min : number,
  out_max : number,
  clamp   : boolean = true
): number
{
  const diff = v_max - v_min;
  const denom = Math.abs( diff ) < 1e-6 ? ( diff < 0 ? -1e-6 : 1e-6 ) : diff;
  let ratio_val = ( value - v_min ) / denom;

  if ( clamp )
    ratio_val = ratio_val < 0 ? 0 : ( ratio_val > 1 ? 1 : ratio_val );

  return out_min + ( ratio_val * ( out_max - out_min ) );
}

/** Ремап числа в діапазон значень Vector2 (підтримує як 2D Vector2, так і THREE.Vector2) */
export function remapVector2(
  value   : number,
  in_min  : number,
  in_max  : number,
  out_min : THREE.Vector2,
  out_max : THREE.Vector2,
  clamp  ?: boolean,
  out    ?: THREE.Vector2
): THREE.Vector2;
export function remapVector2(
  value   : number,
  v_min   : number,
  v_max   : number,
  out_min : Vector2,
  out_max : Vector2,
  clamp  ?: boolean,
  out    ?: Vector2
): Vector2;
export function remapVector2(
  value   : number,
  v_min   : number,
  v_max   : number,
  out_min : Vector2 | THREE.Vector2,
  out_max : Vector2 | THREE.Vector2,
  clamp   : boolean = true,
  out    ?: Vector2 | THREE.Vector2
): Vector2 | THREE.Vector2
{
  const diff = v_max - v_min;
  const denom = Math.abs( diff ) < 1e-6 ? ( diff < 0 ? -1e-6 : 1e-6 ) : diff;
  let ratio_val = ( value - v_min ) / denom;

  if ( clamp )
    ratio_val = ratio_val < 0 ? 0 : ( ratio_val > 1 ? 1 : ratio_val );

  if ( out_min instanceof THREE.Vector2 && out_max instanceof THREE.Vector2 )
  {
    const target = ( out instanceof THREE.Vector2 ) ? out : new THREE.Vector2();
    return target.lerpVectors( out_min, out_max, ratio_val );
  }

  if ( out instanceof Vector2 )
  {
    out.x = out_min.x + ratio_val * ( out_max.x - out_min.x );
    out.y = out_min.y + ratio_val * ( out_max.y - out_min.y );
    return out;
  }

  return new Vector2(
    out_min.x + ratio_val * ( out_max.x - out_min.x ),
    out_min.y + ratio_val * ( out_max.y - out_min.y ),
  );
}

/** Ремап числа в діапазон значень Vector3 (підтримує як об'єкт {x,y,z}, так і THREE.Vector3) */
export function remapVector3(
  value   : number,
  in_min  : number,
  in_max  : number,
  out_min : THREE.Vector3,
  out_max : THREE.Vector3,
  clamp  ?: boolean,
  out    ?: THREE.Vector3
): THREE.Vector3;
export function remapVector3(
  value   : number,
  v_min   : number,
  v_max   : number,
  out_min : Vector3,
  out_max : Vector3,
  clamp  ?: boolean,
  out    ?: Vector3
): Vector3;
export function remapVector3(
  value   : number,
  v_min   : number,
  v_max   : number,
  out_min : Vector3 | THREE.Vector3,
  out_max : Vector3 | THREE.Vector3,
  clamp   : boolean = true,
  out    ?: Vector3 | THREE.Vector3
): Vector3 | THREE.Vector3
{
  const diff = v_max - v_min;
  const denom = Math.abs( diff ) < 1e-6 ? ( diff < 0 ? -1e-6 : 1e-6 ) : diff;
  let ratio_val = ( value - v_min ) / denom;

  if ( clamp )
    ratio_val = ratio_val < 0 ? 0 : ( ratio_val > 1 ? 1 : ratio_val );

  if ( out_min instanceof THREE.Vector3 && out_max instanceof THREE.Vector3 )
  {
    const target = ( out instanceof THREE.Vector3 ) ? out : new THREE.Vector3();
    return target.lerpVectors( out_min, out_max, ratio_val );
  }

  if ( out && !( out instanceof THREE.Vector3 ) )
  {
    out.x = out_min.x + ratio_val * ( out_max.x - out_min.x );
    out.y = out_min.y + ratio_val * ( out_max.y - out_min.y );
    out.z = out_min.z + ratio_val * ( out_max.z - out_min.z );
    return out;
  }

  return {
    x: out_min.x + ratio_val * ( out_max.x - out_min.x ),
    y: out_min.y + ratio_val * ( out_max.y - out_min.y ),
    z: out_min.z + ratio_val * ( out_max.z - out_min.z ),
  };
}

/** Ремап числа в діапазон значень Vector4 */
export function remapVector4(
  value   : number,
  v_min   : number,
  v_max   : number,
  out_min : Vector4,
  out_max : Vector4,
  clamp   : boolean = true,
  out    ?: Vector4
): Vector4
{
  const diff = v_max - v_min;
  const denom = Math.abs( diff ) < 1e-6 ? ( diff < 0 ? -1e-6 : 1e-6 ) : diff;
  let ratio_val = ( value - v_min ) / denom;

  if ( clamp )
    ratio_val = ratio_val < 0 ? 0 : ( ratio_val > 1 ? 1 : ratio_val );

  const target = out ?? { x: 0, y: 0, z: 0, w: 0 };
  target.x = out_min.x + ratio_val * ( out_max.x - out_min.x );
  target.y = out_min.y + ratio_val * ( out_max.y - out_min.y );
  target.z = out_min.z + ratio_val * ( out_max.z - out_min.z );
  target.w = out_min.w + ratio_val * ( out_max.w - out_min.w );
  return target;
}

export function randomOffset2DTowardQuadrant( min: number, max: number, spawn: THREE.Vector2 ): THREE.Vector2
{
  if ( min > max )
    [min, max] = [max, min];

  // Квадрант за знаком координат
  const sx: 1 | -1 = spawn.x >= 0 ? 1 : -1;
  const sy: 1 | -1 = spawn.y >= 0 ? 1 : -1;

  // Базовий напрям — від (0,0) до spawn
  let dx = spawn.x, dy = spawn.y;
  let len = Math.hypot( dx, dy );
  if ( len === 0 )
    { dx = sx; dy = sy; len = 1; }
  dx /= len; dy /= len;

  // Випадковий кут відхилення в межах ±60°
  const jitter = randomSign() * randomRange( 0, Math.PI / 3 );
  const c = Math.cos( jitter );
  const s = Math.sin( jitter );

  // Повертаємо базовий напрям на випадковий кут
  let dirx = dx * c - dy * s;
  let diry = dx * s + dy * c;

  // Гарантуємо правильний квадрант (щоб летіла "в бік кусочка")
  if ( ( dirx >= 0 ? 1 : -1 ) !== sx ) dirx = -dirx;
  if ( ( diry >= 0 ? 1 : -1 ) !== sy ) diry = -diry;

  // Довжина зміщення
  const r = randomRange( min, max );

  return new THREE.Vector2( dirx * r, diry * r );
}

/** Повертає випадкову точку всередині круга (2D) */
export function randomPointInCircle( center: Vector2, radius: number ): Vector2
{
  // Випадковий кут
  const a = randomRange( 0, Math.PI * 2 );
  const r = Math.sqrt( Math.random() ) * radius;

  return new Vector2(
    center.x + r * Math.cos( a ),
    center.y + r * Math.sin( a )
  );
}

/** Повертає випадкову точку всередині круга в 3D просторі (THREE.Vector3) */
export function randomPointOnCircle( center: THREE.Vector3, radius: number, normal: THREE.Vector3 = new THREE.Vector3( 0, 0, 1 ) ): THREE.Vector3
{
  const angle = Math.random() * Math.PI * 2;
  const random_value = Math.random();
  const r = Math.sqrt( random_value ) * radius;

  const point = new THREE.Vector3(
    r * Math.cos( angle ),
    r * Math.sin( angle ),
    0
  );

  const normalized_normal = normal.clone().normalize();
  const quaternion = new THREE.Quaternion().setFromUnitVectors( new THREE.Vector3( 0, 0, 1 ), normalized_normal );

  point.applyQuaternion( quaternion );
  point.add( center );

  return point;
}

/**
  Повертає випадкову точку на контурі круга із заданою товщиною.
  Підтримує як 2D Vector2, так і 3D THREE.Vector3.
 */
export function randomPointOnCircleContour( center: THREE.Vector3, radius: number, thickness: number, normal?: THREE.Vector3 ): THREE.Vector3;
export function randomPointOnCircleContour( center: Vector2, radius: number, thickness?: number ): Vector2;
export function randomPointOnCircleContour(
  center    : Vector2 | THREE.Vector3,
  radius    : number,
  thickness : number = 1,
  normal    : THREE.Vector3 = new THREE.Vector3( 0, 0, 1 )
): Vector2 | THREE.Vector3
{
  if ( center instanceof THREE.Vector3 )
  {
    const angle = Math.random() * Math.PI * 2;
    const inner_radius = Math.max( 0, radius - thickness );
    const min_r_squared = inner_radius * inner_radius;
    const max_r_squared = radius * radius;
    const random_r_squared = min_r_squared + Math.random() * ( max_r_squared - min_r_squared );
    const random_r = Math.sqrt( random_r_squared );

    const point = new THREE.Vector3(
      random_r * Math.cos( angle ),
      random_r * Math.sin( angle ),
      0
    );

    const normalized_normal = normal.clone().normalize();
    const quaternion = new THREE.Quaternion().setFromUnitVectors( new THREE.Vector3( 0, 0, 1 ), normalized_normal );

    point.applyQuaternion( quaternion );
    point.add( center );

    return point;
  }

  const angle = randomRange( 0, Math.PI * 2 );
  const inner_radius = Math.max( 0, radius - thickness );
  const min_r_squared = inner_radius * inner_radius;
  const max_r_squared = radius * radius;
  const random_r_squared = min_r_squared + Math.random() * ( max_r_squared - min_r_squared );
  const random_r = Math.sqrt( random_r_squared );

  return new Vector2(
    center.x + random_r * Math.cos( angle ),
    center.y + random_r * Math.sin( angle ),
  );
}

/** Обчислює вектор відстані для радіального разльоту частинки від центра спавну */
export function computeRadialFlightDistance( center: THREE.Vector2, particles_position: THREE.Vector2, fly_distance: number ): THREE.Vector2
{
  let offset = particles_position.clone().sub( center );
  let len = offset.length();
  if ( len === 0 )
  {
    const angle = Math.random() * Math.PI * 2;
    offset = new THREE.Vector2( Math.cos( angle ), Math.sin( angle ) );
    len = 1;
  }
  const direction = offset.divideScalar( len );

  return direction.multiplyScalar( fly_distance );
}

/** Повертає рандомну точку в області в формі циліндра */
export function randomPointInCylinder( center: THREE.Vector3, radius: number, height: number ): THREE.Vector3
{
  const random_theta = randomRange( 0, 2 * Math.PI );
  const random_value = randomRange( 0, 1 );
  const random_rho = radius * Math.sqrt( random_value );

  const x = random_rho * Math.cos( random_theta );
  const y = random_rho * Math.sin( random_theta );
  const z = randomRange( 0, height );

  return center.clone().add( new THREE.Vector3( x, y, z ) );
}

/** Повертає випадкову точку всередині овалу (еліпса) з рівномірним розподілом за площею */
export function randomPointInEllipse( center: Vector2, radius_x: number, radius_y: number ): Vector2
{
  const theta = randomRange( 0, Math.PI * 2 );
  const r = Math.sqrt( Math.random() );

  const x = r * Math.cos( theta ) * radius_x;
  const y = r * Math.sin( theta ) * radius_y;

  return new Vector2(
    center.x + x,
    center.y + y
  );
}

/**
 * Повертає випадкову точку всередині прямокутної області з заданим кутом нахилу.
 * @param position - центр прямокутника
 * @param size     - розміри прямокутника
 * @param angle    - кут нахилу в радіанах
 */
export function randomPointInRect(
  position : Vector2,
  size     : Vector2,
  angle    : number,
): Vector2
{
  const lx = randomRange( -size.x * 0.5, size.x * 0.5 );
  const ly = randomRange( -size.y * 0.5, size.y * 0.5 );

  const cos_a = Math.cos( angle );
  const sin_a = Math.sin( angle );

  return new Vector2(
    position.x + lx * cos_a - ly * sin_a,
    position.y + lx * sin_a + ly * cos_a,
  );
}

/**
 * Повертає випадкову точку всередині контуру заданої товщини навколо прямокутника,
 * а також нормаль, що вказує назовні від прямокутника.
 */
export function randomPointOnRectContour(
  inner_rect: Rect,
  contour_width: number
): { point: Vector2, normal: Vector2 }
{
  const ix1 = inner_rect.getX();
  const iy1 = inner_rect.getY();
  const iw  = inner_rect.getW();
  const ih  = inner_rect.getH();
  const ix2 = ix1 + iw;
  const iy2 = iy1 + ih;

  const top_area    = ( iw + 2 * contour_width ) * contour_width;
  const bottom_area = ( iw + 2 * contour_width ) * contour_width;
  const left_area   = contour_width * ih;
  const right_area  = contour_width * ih;

  const total = top_area + bottom_area + left_area + right_area;
  let r = randomRange( 0, total );

  if ( r < top_area )
  {
    const x = randomRange( ix1 - contour_width, ix2 + contour_width );
    const y = randomRange( iy1 - contour_width, iy1 );
    return { point: new Vector2( x, y ), normal: new Vector2( 0, -1 ) };
  }
  r -= top_area;

  if ( r < bottom_area )
  {
    const x = randomRange( ix1 - contour_width, ix2 + contour_width );
    const y = randomRange( iy2, iy2 + contour_width );
    return { point: new Vector2( x, y ), normal: new Vector2( 0, 1 ) };
  }
  r -= bottom_area;

  if ( r < left_area )
  {
    const x = randomRange( ix1 - contour_width, ix1 );
    const y = randomRange( iy1, iy2 );
    return { point: new Vector2( x, y ), normal: new Vector2( -1, 0 ) };
  }

  const x = randomRange( ix2, ix2 + contour_width );
  const y = randomRange( iy1, iy2 );
  return { point: new Vector2( x, y ), normal: new Vector2( 1, 0 ) };
}

/** Обчислює вектор зміщення для радіального розльоту частинки від центру спавну */
export function radialOffset( center: Vector2, particles_position: Vector2, fly_distance: Vector2 ): Vector2
{
  let offset = particles_position.subtract( center );
  let len = offset.length();
  if ( len === 0 )
  {
    const angle = Math.random() * Math.PI * 2;
    offset = new Vector2( Math.cos( angle ), Math.sin( angle ) );
    len = 1;
  }
  const direction = offset.divide( len );
  direction.multiplyThis_elementByElement( fly_distance );

  return direction;
}

/** Повертає випадковий напрямок руху */
export function randomMoveDirection(): MoveDirection
{
  const directions_list = Object.values( MoveDirection ).filter( value => typeof value === 'number' ) as MoveDirection[];
  const random_index = Math.floor( Math.random() * directions_list.length );

  return directions_list[random_index];
}

/** Обчислює кут лінії між двома точками в радіанах */
export function lineAngle( start: Vector2, end: Vector2 ): number
{
  return Math.atan2( end.y - start.y, end.x - start.x );
}

/** Обчислює координати точки на заданій відстані під певним кутом від початкової точки */
export function getPointAtAngle( p0: Vector2, angle: number, length: number ): Vector2
{
  return new Vector2( p0.x + Math.cos( angle ) * length, p0.y + Math.sin( angle ) * length );
}

/** Нормалізує колір IRGBColor (0-255) у формат Vector3 (0.0-1.0) */
export function normalizeColorToVec3( color: IRGBColor ): Vector3
{
  return {
    x: color.r / 255,
    y: color.g / 255,
    z: color.b / 255,
  };
}

/** Нормалізує колір IRGBAColor (0-255 RGB, 0-100 A) у формат Vector4 (0.0-1.0) */
export function normalizeColorToVec4( color: IRGBAColor ): Vector4
{
  return {
    ...normalizeColorToVec3( color ),
    w: color.a / 100,
  };
}

/**
 * Створює композитне зображення (знімок) UI-елемента та всіх його дітей на окремому канвасі.
 * 
 * @param element UI-елемент (UIImage), з якого потрібно зробити знімок.
 * @param offset_x Горизонтальний відступ (padding) для розширення канвасу з обох боків.
 * @param offset_y Вертикальний відступ (padding) для розширення канвасу зверху та знизу.
 * @param old_ctx Оригінальний контекст малювання, який буде відновлено для елемента після завершення знімка.
 * @returns HTMLCanvasElement із відрендереним елементом та його дітьми.
 */
export function compositeElement( element: UIImage, offset_x: number, offset_y: number, old_ctx: CanvasRenderingContext2D ): HTMLCanvasElement
{
  const w = element.getRectW();
  const h = element.getRectH();
  const expanded_w = w + offset_x * 2;
  const expanded_h = h + offset_y * 2;

  const composite = document.createElement( "canvas" );
  composite.width  = expanded_w;
  composite.height = expanded_h;
  const ctx = composite.getContext( "2d" )!;

  element.setContextRecursive( ctx );
  element.invalidateSubtree();

  ctx.save();
  ctx.translate( offset_x + w / 2, offset_y + h / 2 );

  const img = element.getImage();
  if ( !img )
    throw new Error( "Image not found" );

  ctx.drawImage( img, -w / 2, -h / 2, w, h );
  
  element.renderChildren();

  ctx.restore();
  element.setContextRecursive( old_ctx );

  return composite;
}
